/**
 * Outlet modal configurator component.
 *
 * Modal truck selector that captures truck info + email, then submits to
 * the backend API and Klaviyo.
 *
 * Four UI states:
 *  - form      : cascading truck dropdowns
 *  - email     : email capture after truck selected
 *  - thankYou  : confirmation after email submitted
 *  - confirmed : truck already in localStorage → shows saved truck, option to change
 */

const OUTLET_API = 'https://heroku1.dback.work';

class ConfiguratorOutlet extends (window.ConfiguratorBase || HTMLElement) {
  #selection = null;

  getSource() { return 'outlet'; }

  cacheAdditionalRefs() {
    this._formView = this.querySelector('[data-ref="formView"]');
    this._emailView = this.querySelector('[data-ref="emailView"]');
    this._thankYouView = this.querySelector('[data-ref="thankYouView"]');
    this._confirmedView = this.querySelector('[data-ref="confirmedView"]');
    this._backdrop = this.querySelector('[data-ref="backdrop"]');
    this._closeBtn = this.querySelector('[data-ref="closeBtn"]');
    this._selectedTruckDisplay = this.querySelector('[data-ref="selectedTruckDisplay"]');
    this._emailInput = this.querySelector('[data-ref="emailInput"]');
    this._submitBtn = this.querySelector('[data-ref="submitBtn"]');
    this._backBtn = this.querySelector('[data-ref="backBtn"]');
    this._doneBtn = this.querySelector('[data-ref="doneBtn"]');
    this._confirmedTruckValue = this.querySelector('[data-ref="confirmedTruckValue"]');
    this._notMyTruckBtn = this.querySelector('[data-ref="notMyTruckBtn"]');
    this._confirmedCloseBtn = this.querySelector('[data-ref="confirmedCloseBtn"]');

    this.#checkUrlParam();
  }

  bindAdditionalEvents() {
    this._closeBtn?.addEventListener('click', () => this.#close());
    this._backdrop?.addEventListener('click', () => this.#close());
    this._doneBtn?.addEventListener('click', () => this.#close());
    this._confirmedCloseBtn?.addEventListener('click', () => this.#close());
    this._backBtn?.addEventListener('click', () => this.#showFormState());
    this._submitBtn?.addEventListener('click', () => this.#handleEmailSubmit());
    this._emailInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.#handleEmailSubmit();
    });
    this._notMyTruckBtn?.addEventListener('click', () => this._clearTruck());

    document.addEventListener('click', (e) => {
      if (e.target.closest('.global-truck-selector__trigger-btn, .configurator-outlet__open-btn')) {
        this.#open();
      }
    });
  }

  async onSelectionComplete(selection, urls) {
    this._saveTruck(selection, urls);
    document.dispatchEvent(new CustomEvent('configurator:update', { detail: { selection, urls } }));
    this.#selection = selection;
    const display = `${selection.year} | ${selection.make} | ${selection.model} | ${selection.bed} Bed`;
    if (this._selectedTruckDisplay) this._selectedTruckDisplay.textContent = display;
    this.#showEmailView();
  }

  onReset() {
    this.#selection = null;
    if (!this.hasAttribute('hidden')) {
      this.#showFormState();
    }
  }

  async onRestored(_saved, _urls) {
    // No-op: the outlet restores state only when opened, not on page load.
  }

  // -- Open / Close --

  #open() {
    const raw = localStorage.getItem('configuratorTruckInfo');
    if (raw) {
      try {
        const saved = JSON.parse(raw);
        if (saved?.make && saved.make !== 'NA') {
          this.#selection = saved;
          const display = `${saved.year} | ${saved.make} | ${saved.model} | ${saved.bed} Bed`;
          if (this._selectedTruckDisplay) this._selectedTruckDisplay.textContent = display;
          this.#showEmailView();
          this.removeAttribute('hidden');
          return;
        }
      } catch { /* ignore */ }
    }
    this.#showFormState();
    this.removeAttribute('hidden');
  }

  #close() {
    this.setAttribute('hidden', '');
  }

  #checkUrlParam() {
    if (new URLSearchParams(window.location.search).get('modal') === 'open') {
      this.#open();
    }
  }

  // -- Email submit --

  async #handleEmailSubmit() {
    const email = this._emailInput?.value?.trim();
    if (!email) return;

    const { make = '', model = '', year = '', bed = '', inbed = '' } = this.#selection || {};
    const params = new URLSearchParams({ email, make, model, year, bed, inbed });
    fetch(`${OUTLET_API}/api/customer/set_customer_truck?${params}`).catch(() => {});
    fetch(`${OUTLET_API}/api/customer/add_tag_to_customer?${new URLSearchParams({ email })}`).catch(() => {});

    if (window._learnq) {
      window._learnq.push(['identify', Object.assign(
        { '$email': email, '$fit_truck_make': make, '$fit_truck_model': model, '$fit_truck_year': year, '$fit_truck_bed': bed },
        inbed ? { '$fit_truck_inbed': inbed } : {}
      )]);
    }

    this.#showThankYouView();
  }

  // -- State transitions --

  #showFormState() {
    this._formView?.removeAttribute('hidden');
    this._emailView?.setAttribute('hidden', '');
    this._thankYouView?.setAttribute('hidden', '');
    this._confirmedView?.setAttribute('hidden', '');
  }

  #showEmailView() {
    this._formView?.setAttribute('hidden', '');
    this._emailView?.removeAttribute('hidden');
    this._thankYouView?.setAttribute('hidden', '');
    this._confirmedView?.setAttribute('hidden', '');
    requestAnimationFrame(() => this._emailInput?.focus());
  }

  #showThankYouView() {
    this._formView?.setAttribute('hidden', '');
    this._emailView?.setAttribute('hidden', '');
    this._thankYouView?.removeAttribute('hidden');
    this._confirmedView?.setAttribute('hidden', '');
  }

  #showConfirmedState(saved) {
    const display = `${saved.year} | ${saved.make} | ${saved.model} | ${saved.bed} Bed`;
    if (this._confirmedTruckValue) this._confirmedTruckValue.textContent = display;
    this._formView?.setAttribute('hidden', '');
    this._emailView?.setAttribute('hidden', '');
    this._thankYouView?.setAttribute('hidden', '');
    this._confirmedView?.removeAttribute('hidden');
  }
}

if (!customElements.get('configurator-outlet-component')) {
  customElements.define('configurator-outlet-component', ConfiguratorOutlet);
}
